import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Container, Grid, makeStyles, Typography } from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import SearchAccordion from '../control/accordion/SearchAccordion'
import Dialog, { DialogType } from '../control/dialog/dialog'
import useAuthenStore from '../stores/authen/authenStore'
import { useQueryParam } from 'use-query-params'
import { useHistory } from 'react-router-dom'
import AgendaCard from '../component/um/settingAgendas/AgendaCard'
import SearchPanel, { SearchForm } from '../component/um/settingAgendas/Panel'
import { AgendaListItem } from '../component/um/settingAgendas/Types'
import { useSettingAgendas, AgendasQuery } from '../services/um/useSettingAgendas'
import useLoading from '../control/loading/useLoading'
import useTranslate from '../i18n/useTranslate'

const useStyles = makeStyles((theme) => ({
    root: { width: '100%' },
    listHeaderRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        margin: theme.spacing(2, 0),
    },
    emptyWrap: {
        border: '1px solid rgba(0,0,0,.125)',
        borderRadius: 12,
        padding: theme.spacing(4),
        textAlign: 'center',
        color: '#6B7280',
        marginTop: theme.spacing(2),
    },
    loadMoreWrap: {
        display: 'flex',
        justifyContent: 'center',
        margin: theme.spacing(2, 0, 4),
    },
}))

const PAGE_SIZE = 10
const IO_ROOT_MARGIN = '100px 0px'
const IO_THRESHOLD = 0

const AgendaListPage: React.FC = () => {
    const authenStore = useAuthenStore()
    const classes = useStyles()
    const loadingHook = useLoading()
    const history = useHistory()
    const t = useTranslate()

    const { getAgendas, deleteAgenda } = useSettingAgendas()

    const [items, setItems] = useState<AgendaListItem[]>([])
    const [total, setTotal] = useState(0)

    const [keyword, setKeyword] = useQueryParam<string | undefined>('keyword')
    const [createdDate, setCreatedDate] = useQueryParam<string | undefined>('createdDate')

    const [page, setPage] = useState(1)
    const [isLoadingFirst, setIsLoadingFirst] = useState(false)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const hasMore = items.length < total

    const loadMoreRef = useRef<HTMLDivElement | null>(null)
    const ioRef = useRef<IntersectionObserver | null>(null)

    const dialogDeleteRef = useRef<Record<string, Function>>()
    const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null)
    const bindDeleteRef = (func: Record<string, Function>) => {
        dialogDeleteRef.current = { ...func }
    }
    const openConfirmDelete = (id: number) => {
        setPendingDeleteId(id)
        dialogDeleteRef.current?.openDialog()
    }

    const initialValues = useMemo<SearchForm>(
        () => ({
            keyword: keyword,
            createdDate: createdDate,
        }),
        [keyword, createdDate],
    )

    useEffect(() => {
        if (authenStore?.profile?.userId) {
            void doSearch(initialValues)
        }
    }, [authenStore?.profile?.userId])

    const fetchPage = useCallback(
        async (targetPage: number, formVal: SearchForm, append: boolean) => {
            const payload: AgendasQuery = {
                search: formVal.keyword?.trim(),
                createdDate: formVal.createdDate || undefined,
                pageNumber: targetPage,
                pageSize: PAGE_SIZE,
            }

            if (!append) {
                setIsLoadingFirst(true)
            } else {
                setIsLoadingMore(true)
            }
            try {
                const res = await getAgendas(String(authenStore?.profile?.irb), payload)
                const nextItems: AgendaListItem[] = res?.result?.items ?? []
                const nextTotal: number = res?.result?.totalRecords ?? 0
                setTotal(nextTotal)

                if (append) {
                    setItems((prev) => [...prev, ...nextItems])
                } else {
                    setItems(nextItems)
                }

                setKeyword(formVal.keyword?.trim() ?? undefined)
                setCreatedDate(formVal.createdDate ?? undefined)
            } finally {
                setIsLoadingFirst(false)
                setIsLoadingMore(false)
            }
        },
        [getAgendas, setKeyword, setCreatedDate],
    )

    const doSearch = useCallback(
        async (formVal: SearchForm) => {
            setPage(1)
            await fetchPage(1, formVal, false)
        },
        [fetchPage],
    )

    const loadNextPage = useCallback(async () => {
        if (isLoadingMore || isLoadingFirst || !hasMore) return
        const next = page + 1
        setPage(next)
        await fetchPage(next, { keyword: keyword ?? '', createdDate: createdDate ?? undefined }, true)
    }, [page, isLoadingMore, isLoadingFirst, hasMore, fetchPage, keyword, createdDate])

    const doSearchWithCurrentFilters = useCallback(
        () => doSearch({ keyword: keyword ?? '', createdDate: createdDate ?? undefined }),
        [doSearch, keyword, createdDate],
    )

    const handleReset = (form: any) => {
        const v: SearchForm = { keyword: '', createdDate: undefined }
        form.initialize(v)
        setKeyword(undefined)
        setCreatedDate(undefined)
        void doSearch(v)
    }

    const onEdit = (id: number) => {
        history.push(`/settingAgendas/${id}/edit`)
    }

    const onCreate = () => {
        history.push('/settingAgendas/create')
    }

    const handleDelete = async () => {
        if (pendingDeleteId == null) return
        loadingHook.loadingOpen()
        try {
            await deleteAgenda(String(authenStore?.profile?.irb), String(pendingDeleteId))
            await doSearchWithCurrentFilters()
        } finally {
            loadingHook.loadingClose()
            setPendingDeleteId(null)
            dialogDeleteRef.current?.closeDialog?.()
        }
    }

    useEffect(() => {
        if (!loadMoreRef.current) return
        if (ioRef.current) {
            ioRef.current.disconnect()
            ioRef.current = null
        }
        const obs = new IntersectionObserver(
            (entries) => {
                const ent = entries[0]
                if (ent?.isIntersecting) {
                    void loadNextPage()
                }
            },
            { root: null, rootMargin: IO_ROOT_MARGIN, threshold: IO_THRESHOLD },
        )
        ioRef.current = obs
        obs.observe(loadMoreRef.current)
        return () => {
            obs.disconnect()
            ioRef.current = null
        }
    }, [loadNextPage, items.length])

    return (
        <Container maxWidth="lg">
            <Grid className={classes.root}>
                <Grid item xs={12}>
                    <SearchAccordion title={t.get('UM.SETTING_AGENDA.MEETING_REPORT')}>
                        <SearchPanel
                            initialValues={initialValues}
                            onSubmit={(formVal) => {
                                setItems([])
                                setTotal(0)
                                setPage(1)
                                void doSearch(formVal)
                            }}
                            onReset={handleReset}
                        />
                    </SearchAccordion>
                </Grid>

                <Grid item xs={12}>
                    <Box className={classes.listHeaderRow}>
                        <Typography variant="h6">{t.get('LABEL.SEARCH_TOTAL_RESULT', [String(total ?? 0)])}</Typography>
                        <Button variant="outlined" color="primary" startIcon={<AddIcon />} onClick={onCreate}>
                            {t.get('UM.SETTING_AGENDA.CREATE_MEETING_REPORT')}
                        </Button>
                    </Box>

                    <Box mt={2} style={{ gap: 20, display: 'flex', flexDirection: 'column' }}>
                        {items.map((it) => (
                            <AgendaCard key={it.agendaId} item={it} onEdit={onEdit} onDelete={openConfirmDelete} />
                        ))}

                        {items.length === 0 && !isLoadingFirst && (
                            <div className={classes.emptyWrap}>
                                <Typography variant="h6">{t.get('LABEL.NO_DATA_FOUND')}</Typography>
                            </div>
                        )}

                        {(isLoadingFirst || isLoadingMore) && (
                            <div className={classes.loadMoreWrap}>
                                <Typography variant="body2" color="textSecondary">
                                    {t.get('UM.SETTING_AGENDA.LOADING')}
                                </Typography>
                            </div>
                        )}

                        {hasMore && !isLoadingMore && (
                            <div className={classes.loadMoreWrap}>
                                <Button onClick={() => void loadNextPage()} variant="outlined">
                                    {t.get('UM.SETTING_AGENDA.LOAD_MORE')}
                                </Button>
                            </div>
                        )}

                        <div ref={loadMoreRef} />
                    </Box>
                </Grid>
            </Grid>

            <Dialog
                onRef={bindDeleteRef}
                type="alert-confirm"
                title={t.get('ALERT.TITLE.WARNING')}
                content={t.get('ALERT.CONTENT.NEED_TO_DELETE')}
                onClickConfirm={handleDelete}
                onAfterClose={() => setPendingDeleteId(null)}
            />
        </Container>
    )
}

export default AgendaListPage
